import { useCallback, useState } from "react";
import { Template } from "@/types/models";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, ToggleLeft, ToggleRight, Search, Trash2 } from "lucide-react";
import { TemplateFormDialog } from "./TemplateFormDialog";
import { usePromptLibraryAdmin, useTemplateAssociations } from "@/hooks/usePromptLibrary";
import { useAdminTemplateTypes, useAdminTemplateFormatTypes } from "@/hooks/useMasterData";
import { useTemplateAdmin } from "@/hooks/useTemplates";
import { useTemplateUsageCount } from "@/hooks/useProjects";
import { useFilterSort } from "@/hooks/useFilterSort";
import { promptLibraryApi } from "@/services/promptLibraryApi";
import { projectTypeVariant } from "@/config/projectTypeConfig";
import DeleteConfirmationDialog from "@/components/DeleteConfirmationDialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface TemplatesTableProps {
  items: Template[];
  isLoading?: boolean;
  onUpdate: (id: string, data: Partial<Template>) => Promise<unknown>;
  onDelete?: (id: string) => Promise<unknown>;
}

export default function TemplatesTable({
  items,
  isLoading,
  onUpdate,
  onDelete,
}: TemplatesTableProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Template | null>(null);
  const [deactivateConfirm, setDeactivateConfirm] = useState<Template | null>(null);
  const [deletingTemplate, setDeletingTemplate] = useState<Template | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const { remove } = useTemplateAdmin();
  const { data: templateUsage } = useTemplateUsageCount(deletingTemplate?.id);

  const { update: updatePromptLibrary } = usePromptLibraryAdmin();
  const { data: templateTypes = [] } = useAdminTemplateTypes();
  const { data: formatTypes = [] } = useAdminTemplateFormatTypes();

  const getSearchableText = useCallback(
    (t: Template) => [t.name, t.description ?? ""],
    []
  );
  const getTemplateTypeId = useCallback((t: Template) => t.template_type_id, []);
  const getFormatTypeIds = useCallback((t: Template) => t.template_formatetype_ids, []);
  const getIsActive = useCallback((t: Template) => t.is_active, []);

  const {
    cycleSort,
    getSortIcon,
    searchText,
    setSearchText,
    filterTemplateType,
    setFilterTemplateType,
    filterFormatType,
    setFilterFormatType,
    filterStatus,
    setFilterStatus,
    sortedItems,
    templateTypeMap,
    formatTypeMap,
  } = useFilterSort<Template>({
    items,
    templateTypes,
    formatTypes,
    getSearchableText,
    getTemplateTypeId,
    getFormatTypeIds,
    getIsActive,
  });

  const handleUpdate = async (values: { name: string; description: string; is_active: boolean; template_type_id: number | null; template_formatetype_ids: number[] }) => {
    if (!editing) return;
    await onUpdate(editing.id, {
      name: values.name,
      description: values.description || null,
      is_active: values.is_active,
      template_type_id: values.template_type_id,
      template_formatetype_ids: values.template_formatetype_ids,
    });
    setEditing(null);
  };

  const handleToggleActive = async (template: Template) => {
    if (template.is_active) {
      setDeactivateConfirm(template);
    } else {
      await onUpdate(template.id, { is_active: true });
    }
  };

  const handleConfirmDeactivate = async () => {
    if (!deactivateConfirm) return;

    const promptLibraries = await promptLibraryApi.getPromptLibrariesByTemplate(deactivateConfirm.id);
    await Promise.all(
      promptLibraries
        .filter(pl => pl.is_active)
        .map(pl => updatePromptLibrary.mutateAsync({ id: pl.id, data: { is_active: false } }))
    );
    await onUpdate(deactivateConfirm.id, { is_active: false });
    setDeactivateConfirm(null);
  };

  const openEditDialog = (template: Template) => {
    setEditing(template);
    setDialogOpen(true);
  };

  const openCreateDialog = () => {
    setEditing(null);
    setDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingTemplate) return;
    setIsDeleting(true);
    try {
      if (onDelete) {
        await onDelete(deletingTemplate.id);
      } else {
        await remove.mutateAsync(deletingTemplate.id);
      }
    } finally {
      setIsDeleting(false);
      setDeletingTemplate(null);
    }
  };

  return (
    <>
      <div className="space-y-4">
        {/* Filter Block */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search templates..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="pl-8"
            />
          </div>
          <Select value={filterTemplateType} onValueChange={setFilterTemplateType}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Template Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Template Types</SelectItem>
              {templateTypes.map((tt) => (
                <SelectItem key={tt.id} value={String(tt.id)}>{tt.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterFormatType} onValueChange={setFilterFormatType}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Format Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Format Types</SelectItem>
              {formatTypes.map((ft) => (
                <SelectItem key={ft.id} value={String(ft.id)}>{ft.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={openCreateDialog} size="sm" className="bg-brand-teal">
            <Plus className="w-4 h-4 mr-2" />
            Add Template
          </Button>
        </div>

        {isLoading ? (
          <div className="text-center py-8 text-muted-foreground">Loading...</div>
        ) : sortedItems.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No templates found. Click "Add Template" to create one.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="cursor-pointer select-none" onClick={() => cycleSort("name")}>
                  <div className="flex items-center gap-1">
                    Name
                    {getSortIcon("name")}
                  </div>
                </TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => cycleSort("templateType")}>
                  <div className="flex items-center gap-1">
                    Template Type
                    {getSortIcon("templateType")}
                  </div>
                </TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => cycleSort("formatTypes")}>
                  <div className="flex items-center gap-1">
                    Format Types
                    {getSortIcon("formatTypes")}
                  </div>
                </TableHead>
                <TableHead className="w-[100px] cursor-pointer select-none" onClick={() => cycleSort("status")}>
                  <div className="flex items-center gap-1">
                    Status
                    {getSortIcon("status")}
                  </div>
                </TableHead>
                <TableHead className="w-[100px]">Columns</TableHead>
                <TableHead className="w-[200px] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedItems.map((template) => (
                <TemplateRow
                  key={template.id}
                  template={template}
                  templateTypeMap={templateTypeMap}
                  formatTypeMap={formatTypeMap}
                  onEdit={openEditDialog}
                  onToggle={handleToggleActive}
                  onDelete={setDeletingTemplate}
                />
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <TemplateFormDialog
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) setEditing(null);
        }}
        defaultValues={editing ?? undefined}
        onSave={editing ? handleUpdate : undefined}
      />

      <DeleteConfirmationDialog
        open={!!deletingTemplate}
        title="Delete Template"
        description="This will permanently delete this template and remove all prompt associations. This action cannot be undone."
        itemName={deletingTemplate?.name}
        warningText={
          templateUsage && templateUsage.usage_count > 0
            ? `This template is used by ${templateUsage.usage_count} project(s). Deleting it will not affect those projects' own copies.`
            : undefined
        }
        isDeleting={isDeleting}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeletingTemplate(null)}
      />

      <AlertDialog open={!!deactivateConfirm} onOpenChange={(open) => !open && setDeactivateConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate Template</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to proceed?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>No</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDeactivate}>
              Yes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function TemplateRow({
  template,
  templateTypeMap,
  formatTypeMap,
  onEdit,
  onToggle,
  onDelete,
}: {
  template: Template;
  templateTypeMap: Map<number, string>;
  formatTypeMap: Map<number, string>;
  onEdit: (template: Template) => void;
  onToggle: (template: Template) => void;
  onDelete: (template: Template) => void;
}) {
  const { data: associations = [] } = useTemplateAssociations(template.id);
  const promptCount = associations.length;

  return (
    <TableRow>
      <TableCell className="font-medium">{template.name}</TableCell>
      <TableCell className="text-muted-foreground">
        {template.description || <span className="italic">No description</span>}
      </TableCell>
      <TableCell>
        {(() => {
          const typeName = templateTypeMap.get(template.template_type_id ?? -1);
          return typeName ? (
            <Badge variant={projectTypeVariant[typeName.toUpperCase()] ?? "secondary"}>{typeName}</Badge>
          ) : (
            <span className="text-muted-foreground">-</span>
          );
        })()}
      </TableCell>
      <TableCell className="text-muted-foreground">
        {template.template_formatetype_ids?.length
          ? template.template_formatetype_ids
              .map((id: number) => formatTypeMap.get(id))
              .filter(Boolean)
              .join(", ")
          : "-"}
      </TableCell>
      <TableCell>
        <Badge variant={template.is_active ? "default" : "secondary"}>
          {template.is_active ? "Active" : "Inactive"}
        </Badge>
      </TableCell>
      <TableCell className="text-center">
        {promptCount}
      </TableCell>
      <TableCell className="text-right">
        <div className="flex justify-end gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onEdit(template)}
            title="Edit"
          >
            <Pencil className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onToggle(template)}
            title={template.is_active ? "Deactivate" : "Activate"}
          >
            {template.is_active ? (
              <ToggleRight className="w-4 h-4" />
            ) : (
              <ToggleLeft className="w-4 h-4" />
            )}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onDelete(template)}
            title="Delete"
            className="text-destructive hover:text-destructive"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}
